import styled from 'styled-components';
import { ComponentSearchData, ComponentSelectionMode } from './types';
import { ComponentRepresentation } from '@ardoq/basic-components';
import { IconSize } from '@ardoq/icons';
import {
  Table,
  type Column,
  type Row,
  type SelectableProps,
  type SortDescriptor,
} from '@ardoq/table';
import { TruncatedTextPopover } from '@ardoq/popovers';
import { FlexBox } from '@ardoq/layout';
import { EnhancedRepresentationData } from 'modelInterface/prototype/types';
import { t, type I18nLocale } from '@ardoq/i18n';
import { colors } from '@ardoq/design-tokens';
import { getSelectionColumnLabels } from 'locales/getTableLabels';

type ComponentNameCellData = {
  name: string;
  representation: EnhancedRepresentationData;
};

type TextCellData = { value: string };

type RowData = {
  name: ComponentNameCellData;
  componentType: TextCellData;
  workspace: TextCellData;
  updatedBy: TextCellData;
  createdBy: TextCellData;
};

type SelectableComponentListProps = {
  components: ComponentSearchData[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  /**
   * When `true`, row checkboxes are rendered visually disabled and cannot be
   * toggled. Used while an external "Select all matching the query" control
   * owns the entire selection — row-level toggles would only operate on the
   * loaded page and misrepresent the selection.
   */
  lockRowSelection?: boolean;
  /** `selectedIds` keeps its array shape in `'single'` mode. */
  selectionMode?: ComponentSelectionMode;
  i18nLocale: I18nLocale;
  sortDescriptor: SortDescriptor | null;
  onSortDescriptorChange: (descriptor: SortDescriptor | null) => void;
};

const ComponentNameCell = ({ name, representation }: ComponentNameCellData) => (
  <FlexBox align="center" gap="small">
    <ComponentRepresentation
      isImage={representation.isImage ?? false}
      value={representation.value ?? null}
      icon={representation.icon}
      iconSize={IconSize.LARGE}
      style={{
        fontSize: IconSize.LARGE,
        color: representation.contrastColor,
      }}
    />
    <TruncatedTextPopover>{name}</TruncatedTextPopover>
  </FlexBox>
);

const TextOverflowCell = ({ value }: TextCellData) => (
  <TruncatedTextPopover>{value}</TruncatedTextPopover>
);

const getNameValue = ({ name }: ComponentNameCellData): string => name;
const getTextValue = ({ value }: TextCellData): string => value;

const toRows = (components: ComponentSearchData[]): Row<RowData>[] =>
  components.map(component => ({
    id: component.id,
    data: {
      name: {
        name: component.name,
        representation: component.representationData,
      },
      componentType: { value: component.entityType },
      workspace: { value: component.rootWorkspaceName },
      updatedBy: { value: component.lastModifiedByName },
      createdBy: { value: component.createdByName },
    },
  }));

const getColumns = (i18nLocale: I18nLocale): Column<RowData>[] => [
  {
    title: t(i18nLocale, 'viewpointBuilder:selectContext.column.name', 'Name'),
    id: 'name',
    flex: 35,
    cellRenderer: ComponentNameCell,
    valueGetter: getNameValue,
  },
  {
    title: t(
      i18nLocale,
      'viewpointBuilder:selectContext.column.componentType',
      'Component type'
    ),
    id: 'componentType',
    flex: 15,
    cellRenderer: TextOverflowCell,
    valueGetter: getTextValue,
  },
  {
    title: t(
      i18nLocale,
      'viewpointBuilder:selectContext.column.workspace',
      'Workspace'
    ),
    id: 'workspace',
    flex: 20,
    cellRenderer: TextOverflowCell,
    valueGetter: getTextValue,
  },
  {
    title: t(
      i18nLocale,
      'viewpointBuilder:selectContext.column.updatedBy',
      'Updated by'
    ),
    id: 'updatedBy',
    flex: 15,
    cellRenderer: TextOverflowCell,
    valueGetter: getTextValue,
  },
  {
    title: t(
      i18nLocale,
      'viewpointBuilder:selectContext.column.createdBy',
      'Created by'
    ),
    id: 'createdBy',
    flex: 15,
    cellRenderer: TextOverflowCell,
    valueGetter: getTextValue,
  },
];

type SelectableConfigArgs = {
  selectionMode: ComponentSelectionMode;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  lockRowSelection: boolean;
};

const toSelectableConfig = ({
  selectionMode,
  selectedIds,
  onSelectionChange,
  lockRowSelection,
}: SelectableConfigArgs): SelectableProps<string, RowData> | undefined => {
  if (selectionMode === 'multiple-no-checkboxes') return undefined;
  if (selectionMode === 'single') {
    return {
      mode: 'single',
      id: selectedIds[0] ?? null,
      onChange: id => onSelectionChange(id ? [id] : []),
    };
  }
  return {
    mode: 'multiple',
    ids: selectedIds,
    onChange: onSelectionChange,
    hideHeaderCheckbox: true,
    enableRowClickSelection: !lockRowSelection,
    getRowLabel: row => row.name.name,
  };
};

const SelectableComponentList = ({
  components,
  selectedIds,
  onSelectionChange,
  lockRowSelection = false,
  selectionMode = 'multiple',
  i18nLocale,
  sortDescriptor,
  onSortDescriptorChange,
}: SelectableComponentListProps) => {
  if (components.length === 0) return null;
  return (
    <Wrapper $lockRowSelection={lockRowSelection}>
      <Table<RowData>
        aria-label={t(
          i18nLocale,
          'viewpointBuilder:selectContext.list.ariaLabel',
          'Component search results'
        )}
        rows={toRows(components)}
        columns={getColumns(i18nLocale)}
        sortable={{
          descriptor: sortDescriptor,
          onChange: onSortDescriptorChange,
        }}
        selectable={toSelectableConfig({
          selectionMode,
          selectedIds,
          onSelectionChange,
          lockRowSelection,
        })}
        selectionColumnLabels={getSelectionColumnLabels(i18nLocale)}
        height="100%"
      />
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $lockRowSelection: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  ${({ $lockRowSelection }) =>
    $lockRowSelection &&
    `
    .ag-cell[col-id="ag-Grid-SelectionColumn"],
    .ag-header-cell[col-id="ag-Grid-SelectionColumn"] {
      pointer-events: none;
    }
    .ag-cell[col-id="ag-Grid-SelectionColumn"] .ag-checked {
      border-color: ${colors.borderDisabled};
      background-color: ${colors.surfaceDisabled};
    }
    .ag-row-selected:before {
      background-color: ${colors.surfaceDefault};
    }
  `}
`;

export { SelectableComponentList };
